import React, { useState } from "react";
import { Eye } from "lucide-react";
import OrderDetailsDialogbox from "./OrderDetailsDialogbox";
import { TablerCaretUpDownFilled } from "../../assets/icons/icons";
import EmptyTableRecord from "../layout/EmptyTableRecord";
import useSortableData from "../../utils/SortData";
import SearchBar from "../layout/SearchBar";
import { capitalizeWords } from "../../utils/TextFormat";
import { formatDateParts } from "../../utils/DateFormat";
import { getStatusClass } from "../layout/StatusStyleHandling";

// Shared table headers
const TABLE_HEADERS = [
  { key: "uid", label: "ID" },
  { key: "createdAt", label: "Date & time" },
  { key: "customer.mobileNumber", label: "Customer number" },
  { key: "destinationNumber", label: "Destination number" },
  { key: "paymentMethod", label: "Method" },
  { key: "product.brandName", label: "Brand" },
  { key: "product.productName", label: "Product" },
  { key: "customer.referral.code", label: "Referral code" },
  {
    key: "paymentStatus",
    label: "Payment status",
    isStatus: true,
    isPaymentStatus: true
  },
  { key: "orderStatus", label: "Order status", isStatus: true },
  { key: "total.totalAmount", label: "Total(€)" },
  { key: "action", label: "Action", isAction: true }
];

const OrderTable = ({
  orders,
  searchTerm,
  setSearchTerm,
  totalRevenue,
  totalCount,
  mobileStatsOnly = false,
  openModal,
  selectedOrder,
  isModalOpen,
  closeModal,
  updateOrderStatus,
  updatePaymentStatus
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || "");
  const { sortedItems: sortedOrders, handleSort } = useSortableData(orders);

  const handleSearch = () => {
    setSearchTerm(localSearchTerm);
  };

  if (mobileStatsOnly) {
    return (
      <div className="flex items-center">
        <SearchBar
          searchTerm={localSearchTerm}
          onChange={(value) => setLocalSearchTerm(value)}
          onSearch={handleSearch}
          className="w-full"
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center w-full mb-6">
        <div className="border border-gray-200 rounded-lg relative overflow-hidden">
          <div className="flex p-2">
            <div className="px-2 text-center gap-2">
              <div className="text-gray-500 text-xs uppercase font-medium w-fit mb-2">
                TOTAL TRANSACTIONS
              </div>
              <div className="text-lg font-bold text-text1">{totalCount}</div>
            </div>
            <div className="px-2 text-center border-l border-gray-200">
              <div className="text-gray-500 text-xs uppercase font-medium mb-2">
                TOTAL REVENUE
              </div>
              <div className="text-lg font-bold text-text1">
                € {Number(totalRevenue || 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center">
          <SearchBar
            searchTerm={localSearchTerm}
            onChange={(value) => setLocalSearchTerm(value)}
            onSearch={handleSearch}
            className="w-96"
          />
        </div>
      </div>

      <div className="rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto mb-6">
        <table className="w-full border-gray-300 rounded-md">
          <thead className="bg-gray-100 border-b">
            <tr className="bg-gray-100 md:h-10">
              {TABLE_HEADERS.map((header, index) => (
                <th
                  key={index}
                  className="text-center whitespace-nowrap px-2 text-gray-900 text-[15px] font-['Rubik'] font-semibold md:h-10"
                >
                  {!header.isAction ? (
                    <div
                      className="flex items-center cursor-pointer text-left"
                      onClick={() => handleSort(header.key)}
                    >
                      <TablerCaretUpDownFilled className="mr-2" />
                      {header.label}
                    </div>
                  ) : (
                    <div className="flex text-left items-center mr-2">
                      {header.label}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {sortedOrders.length > 0 ? (
              sortedOrders.map((order) => (
                <tr key={order._id} className="hover:bg-gray-50 md:h-[60px]">
                  {TABLE_HEADERS.map((header) => (
                    <td
                      key={header.key}
                      className="px-2 text-left text-gray-700 text-[15px] font-['Rubik'] font-normal md:h-[60px]"
                    >
                      {header.key === "createdAt" ? (
                        <div className="whitespace-nowrap">
                          {(() => {
                            const { date, time } = formatDateParts(
                              order[header.key]
                            );
                            return (
                              <>
                                <div>{date}</div>
                                <div>{time}</div>
                              </>
                            );
                          })()}
                        </div>
                      ) : header.isAction ? (
                        <div className="flex justify-start min-h-6">
                          <div className="flex justify-center">
                            <button onClick={() => openModal(order)}>
                              <Eye className="h-5 w-5 text-gray-400" />
                            </button>
                          </div>
                        </div>
                      ) : header.isStatus ? (
                        <span
                          className={`${getStatusClass(order[header.key])} font-['Rubik'] text-xs font-medium`}
                        >
                          {capitalizeWords(order[header.key] || "")}
                        </span>
                      ) : header.key === "product.productName" ? (
                        <span className="whitespace-nowrap">
                          {order.product?.productName || "N/A"}
                        </span>
                      ) : header.key === "total.totalAmount" ? (
                        (order.total?.totalAmount?.toFixed(2) ?? "0.00")
                      ) : header.key === "customer.referral.code" ? (
                        (order.customer?.referral?.code ?? "N/A")
                      ): header.key === "customer.mobileNumber" ? (
                        ('+'+order.customer?.mobileNumber ?? "N/A")
                      )
                      : header.key === "destinationNumber" ? (
                        (order.destinationNumber ?  `+${order.destinationNumber}` : "N/A")
                      ) : (
                        header.key
                          .split(".")
                          .reduce((o, key) => (o ? o[key] : ""), order) || "N/A"
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={TABLE_HEADERS.length}
                  className="text-center text-gray-500"
                >
                  <EmptyTableRecord />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && selectedOrder && (
        <OrderDetailsDialogbox
          selectedOrder={selectedOrder}
          closeModal={closeModal}
          updateOrderStatus={updateOrderStatus}
          updatePaymentStatus={updatePaymentStatus}
        />
      )}
    </div>
  );
};

export { TABLE_HEADERS };
export default OrderTable;
